import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileSearch, Play, Save, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { shrinkImage } from "../lib/image";
import { useAuth } from "../context/AuthContext";

const fmt = (v, d = 0) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });
const pre = "whitespace-pre-wrap rounded bg-slate-950 p-2 font-mono text-[11px] leading-snug text-slate-200";

const splitLine = (l) => { const t = l.split(/\t|;/); return t.length > 1 ? t : l.trim().split(/\s{2,}/); };

function FirmRows({ f, i, headers, lines, onEdit }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button data-testid={`analiz-rows-toggle-${i}`} onClick={() => setOpen(!open)} className="text-[11px] font-bold text-sky-400 hover:text-sky-300">{open ? "▾" : "▸"} SATIRLAR ({fmt(f.rows)}) — DÜZENLEMEK İÇİN TIKLA</button>
      {open && (
        <div className="mt-1 max-h-72 overflow-auto rounded border border-slate-800">
          <table className="text-[11px]">
            <thead className="sticky top-0 bg-slate-900"><tr>{headers.map((h, ci) => <th key={ci} className="min-w-[110px] px-1 py-1 text-left text-slate-400">{h}</th>)}</tr></thead>
            <tbody>{f.lines.map((li) => { const t = splitLine(lines[li] || ""); return (
              <tr key={li} className="border-t border-slate-800">{headers.map((_, ci) => (
                <td key={ci} className="p-0"><input data-testid={`analiz-cell-${li}-${ci}`} defaultValue={t[ci] || ""} onBlur={(e) => e.target.value !== (t[ci] || "") && onEdit(li, ci, e.target.value)} className="w-full bg-transparent px-1 py-0.5 text-slate-200 focus:bg-slate-800 focus:outline-none" /></td>
              ))}</tr>); })}</tbody>
          </table>
          {f.rows > f.lines.length && <div className="p-1 text-[10px] text-slate-500">İLK {f.lines.length} SATIR GÖSTERİLİYOR</div>}
        </div>
      )}
    </div>
  );
}

function FirmResult({ f, i, rowsProps }) {
  return (
    <div data-testid={`analiz-firm-${i}`} className="space-y-2 rounded-lg border border-slate-800 bg-slate-900 p-3">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-sm font-bold text-white">{f.name}</span>
        <span data-testid={`analiz-firm-status-${i}`} className={`rounded px-2 py-0.5 font-bold ${f.status === "mevcut" ? "bg-emerald-900 text-emerald-200" : "bg-amber-900 text-amber-200"}`}>{f.status === "mevcut" ? "MEVCUT MÜŞTERİ" : "SİSTEMDE YOK"} → {f.hedef}</span>
        <span className="text-slate-400">{f.il}{f.ilce ? ` / ${f.ilce}` : ""}</span>
        <span className="text-slate-300">{fmt(f.rows)} GÖNDERİ · {fmt(f.adet)} ADET · YEKÜN <b className="text-white">{fmt(f.yekun, 2)}</b></span>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <div><div className="mb-1 text-[11px] font-bold text-slate-400">İL CİROLARI</div><div className={pre}>{f.il_ciro_text}</div></div>
        <div><div className="mb-1 text-[11px] font-bold text-slate-400">TEKLİF (BİRİM FİYAT BAREMLERİ)</div><div className={pre}>{f.teklif_text}</div></div>
        <div className="space-y-2">
          {f.cikis_ciro_text && <div><div className="mb-1 text-[11px] font-bold text-slate-400">ÇIKIŞ İL CİROLARI</div><div className={pre}>{f.cikis_ciro_text}</div></div>}
          <div><div className="mb-1 text-[11px] font-bold text-slate-400">RUT</div><div className={pre}>{f.rut_text}</div></div>
        </div>
      </div>
      <div>
        <div className="mb-1 flex items-center gap-2 text-[11px] font-bold text-slate-400">TEKLİF YAZISI
          <button data-testid={`analiz-copy-${i}`} onClick={() => { navigator.clipboard.writeText(f.yazi); toast.success("KOPYALANDI"); }} className="flex items-center gap-1 text-sky-400 hover:text-sky-300"><Copy className="h-3 w-3" /> KOPYALA</button>
        </div>
        <div className="whitespace-pre-wrap rounded bg-slate-800/60 p-2 text-xs text-slate-200">{f.yazi}</div>
      </div>
      {rowsProps && <FirmRows f={f} i={i} {...rowsProps} />}
    </div>
  );
}

const CK = [["firma", "FİRMA"], ["yetkili", "YETKİLİ"], ["gsm", "GSM"], ["s_tel", "S.TEL"], ["email", "EMAIL"], ["web", "WEB SİTESİ"], ["adres", "ADRES"], ["il", "İL"], ["ilce", "İLÇE"]];

const missing = (c) => !(c.gsm || c.s_tel) || !c.adres || !c.il;

function FirmDraft({ d, onChange, targets }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(d.cust);
  const [tg, setTg] = useState("");
  const [scan, setScan] = useState(false);
  const save = async () => { await api.put(`/analiz/firm-drafts/${d.id}`, { data: f }); toast.success("TASLAK GÜNCELLENDİ"); setEdit(false); onChange(); };
  const add = async (target) => { const r = await api.post(`/analiz/firm-drafts/${d.id}/add`, { data: { target } }); toast.success(r.data.detail); onChange(); };
  const del = async () => { if (!window.confirm(`${d.name} TASLAĞI SİLİNSİN Mİ?`)) return; await api.delete(`/analiz/firm-drafts/${d.id}`); onChange(); };
  const readImgs = async (imgs) => {
    if (!imgs.length) return;
    setScan(true);
    try {
      const fd = new FormData();
      for (const im of imgs) fd.append("files", await shrinkImage(im));
      const { data } = await api.post("/customers/scan-cards", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const got = {};
      for (const it of data.items || []) for (const [k] of CK) if (it.data?.[k] && !got[k]) got[k] = it.data[k];
      if (!Object.keys(got).length) { toast.error("GÖRSELDEN BİLGİ OKUNAMADI"); return; }
      setF((p) => ({ ...p, ...got }));
      toast.success("FİRMA BİLGİLERİ DOLDURULDU — KONTROL EDİP KAYDEDİN (HESAPLAMA DEĞİŞMEZ)");
    } catch (e) { toast.error(e.response?.data?.detail || "TARAMA HATASI"); } finally { setScan(false); }
  };
  const onPaste = (e) => { const imgs = [...(e.clipboardData?.items || [])].filter((x) => x.type?.startsWith("image/")).map((x) => x.getAsFile()).filter(Boolean); if (imgs.length) { e.preventDefault(); readImgs(imgs); } };
  return (
    <div data-testid={`analiz-fdraft-${d.id}`} className="border-b border-amber-900/40 py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-white">{d.name}</span>
        <span className="text-slate-400">{d.cust.il}{d.cust.ilce ? ` / ${d.cust.ilce}` : ""}</span>
        <span className={missing(d.cust) ? "text-rose-400" : "text-slate-500"}>{d.cust.gsm || d.cust.s_tel || "TEL YOK"} · {d.cust.adres ? "ADRES VAR" : "ADRES YOK"}</span>
        <span className="text-emerald-400">HESAPLAMA HAZIR · {fmt(d.rows)} GÖNDERİ · {fmt(d.yekun, 2)}</span>
        <span className="flex-1" />
        <Button data-testid={`analiz-fdraft-edit-${d.id}`} onClick={() => setEdit(!edit)} className="h-6 bg-slate-700 px-2 text-[11px] hover:bg-slate-600">DÜZENLE</Button>
        <Button data-testid={`analiz-fdraft-add-${d.id}`} onClick={() => add()} className="h-6 bg-emerald-700 px-2 text-[11px] hover:bg-emerald-600">ANA DEPOYA EKLE</Button>
        {targets.length > 0 && <>
          <select data-testid={`analiz-fdraft-target-${d.id}`} value={tg} onChange={(e) => setTg(e.target.value)} className="h-6 rounded border border-slate-700 bg-slate-800 px-1 text-[11px] text-slate-200"><option value="">FİRMA SEÇ</option>{targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <Button data-testid={`analiz-fdraft-transfer-${d.id}`} disabled={!tg} onClick={() => add(tg)} className="h-6 bg-indigo-700 px-2 text-[11px] hover:bg-indigo-600">FİRMAYA AKTAR</Button>
        </>}
        <Button data-testid={`analiz-fdraft-del-${d.id}`} onClick={del} className="h-6 bg-rose-800 px-2 text-[11px] hover:bg-rose-700">SİL</Button>
      </div>
      {edit && (
        <div onPaste={onPaste} data-testid={`analiz-fdraft-panel-${d.id}`} className="mt-2 grid grid-cols-3 gap-2 rounded bg-slate-900 p-2">
          <label data-testid={`analiz-fdraft-scan-${d.id}`} className="col-span-3 flex cursor-pointer items-center justify-center rounded border border-dashed border-sky-700 py-2 text-[11px] font-bold text-sky-300 hover:bg-sky-950/40">
            {scan ? "GÖRSEL OKUNUYOR…" : "EKRAN GÖRÜNTÜSÜNÜ BURAYA YAPIŞTIRIN (CTRL+V) VEYA TIKLAYIP SEÇİN — FİRMA BİLGİLERİ DOLAR, HESAPLAMA DEĞİŞMEZ"}
            <input type="file" accept="image/*" multiple hidden onChange={(e) => { readImgs([...e.target.files]); e.target.value = ""; }} />
          </label>
          {CK.map(([k, l]) => <Input key={k} data-testid={`analiz-fdraft-${k}-${d.id}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={l} className="h-8 border-slate-700 bg-slate-800 text-xs text-white" />)}
          <Button data-testid={`analiz-fdraft-save-${d.id}`} onClick={save} className="col-span-3 h-8 bg-blue-600 text-xs hover:bg-blue-700">TASLAĞI KAYDET</Button>
        </div>
      )}
    </div>
  );
}

function FirmDrafts({ fdrafts, reload }) {
  const { isSuperAdmin } = useAuth();
  const [targets, setTargets] = useState([]);
  const [onlyMiss, setOnlyMiss] = useState(false);
  const [tg, setTg] = useState("");
  const [prog, setProg] = useState(null);
  useEffect(() => { if (isSuperAdmin) api.get("/analiz/targets").then((r) => setTargets(r.data)); }, [isSuperAdmin]);
  const list = onlyMiss ? fdrafts.filter((d) => missing(d.cust)) : fdrafts;
  const bulk = async (target) => {
    const ids = list.map((d) => d.id);
    if (!window.confirm(`${fmt(ids.length)} FİRMA ${target ? targets.find((t) => t.id === target)?.name + " FİRMASINA AKTARILSIN" : "ANA DEPO MÜŞTERİLERİNE EKLENSİN"} MI?`)) return;
    let n = 0;
    try {
      for (let i = 0; i < ids.length; i += 300) {
        setProg(`${fmt(i)} / ${fmt(ids.length)}`);
        const r = await api.post("/analiz/firm-drafts-bulk", { data: { ids: ids.slice(i, i + 300), target } });
        n += r.data.added;
      }
      toast.success(`${fmt(n)} FİRMA MÜŞTERİLERE EKLENDİ`);
    } catch (e) { toast.error(e.response?.data?.detail || "HATA"); } finally { setProg(null); reload(); }
  };
  return (
    <details open data-testid="analiz-fdrafts" className="rounded-lg border border-amber-800 bg-amber-950/20 p-3 text-xs">
      <summary className="cursor-pointer font-bold text-amber-200">TASLAK FİRMALAR — SİSTEMDE OLMAYANLAR, HESAPLAMASI HAZIR ({fmt(fdrafts.length)}) · DÜZENLE → ANA DEPOYA EKLE / FİRMAYA AKTAR</summary>
      <div className="my-2 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-slate-300"><input data-testid="analiz-fdrafts-onlymiss" type="checkbox" checked={onlyMiss} onChange={(e) => setOnlyMiss(e.target.checked)} /> SADECE EKSİK BİLGİLİLER ({fmt(fdrafts.filter((d) => missing(d.cust)).length)})</label>
        <span className="flex-1" />
        {prog && <span data-testid="analiz-fdrafts-progress" className="font-bold text-sky-300">İŞLENİYOR {prog}</span>}
        <Button data-testid="analiz-fdrafts-add-all" disabled={!!prog || !list.length} onClick={() => bulk()} className="h-7 bg-emerald-700 px-2 text-xs hover:bg-emerald-600">LİSTEDEKİ TÜMÜNÜ ANA DEPOYA EKLE ({fmt(list.length)})</Button>
        {targets.length > 0 && <>
          <select data-testid="analiz-fdrafts-target" value={tg} onChange={(e) => setTg(e.target.value)} className="h-7 rounded border border-slate-700 bg-slate-800 px-1 text-xs text-slate-200"><option value="">FİRMA SEÇ</option>{targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <Button data-testid="analiz-fdrafts-transfer-all" disabled={!!prog || !tg || !list.length} onClick={() => bulk(tg)} className="h-7 bg-indigo-700 px-2 text-xs hover:bg-indigo-600">TÜMÜNÜ FİRMAYA AKTAR</Button>
        </>}
      </div>
      <div className="max-h-96 overflow-auto">{list.slice(0, 500).map((d) => <FirmDraft key={d.id} d={d} onChange={reload} targets={targets} />)}</div>
      {list.length > 500 && <div className="pt-1 text-[10px] text-slate-500">İLK 500 FİRMA GÖSTERİLİYOR — TOPLU İŞLEM LİSTENİN TAMAMINA UYGULANIR</div>}
    </details>
  );
}

export default function AnalizForm() {
  const [firma, setFirma] = useState("");
  const [web, setWeb] = useState("");
  const [text, setText] = useState("");
  const [pv, setPv] = useState(null);
  const [map, setMap] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [fdrafts, setFdrafts] = useState([]);
  const loadDrafts = () => { api.get("/analiz/drafts").then((r) => setDrafts(r.data)); api.get("/analiz/firm-drafts").then((r) => setFdrafts(r.data)); };
  useEffect(() => { loadDrafts(); }, []);

  const preview = async () => { const r = await api.post("/analiz/preview", { data: { text } }); setPv(r.data); setMap(r.data.mapping); setRes(null); };
  const run = async (save) => {
    setBusy(true);
    try {
      const r = await api.post("/analiz/run", { data: { text, mapping: map, firma, web, save } });
      setRes(r.data);
      if (save) { toast.success(`${r.data.firms.filter((x) => x.status === "mevcut").length} MEVCUT KART GÜNCELLENDİ · ${r.data.firms.filter((x) => x.status !== "mevcut").length} YENİ FİRMA TASLAĞA · ${r.data.skipped.length} SATIR TASLAĞA`); loadDrafts(); }
    } finally { setBusy(false); }
  };
  const delDraft = async (id) => { await api.delete(`/analiz/drafts/${id}`); loadDrafts(); };
  const [edits, setEdits] = useState({});
  const [edited, setEdited] = useState(false);
  const editCell = (li, ci, v) => {
    const ls = text.split("\n").filter((l) => l.trim());
    const t = splitLine(ls[li]); while (t.length <= ci) t.push("");
    t[ci] = v; ls[li] = t.join("\t"); setText(ls.join("\n")); setEdited(true);
  };
  const toForm = async (list) => {
    const head = list[0].baslik || "";
    const body = list.filter((d) => (d.baslik || "") === head).map((d) => edits[d.id] ?? d.satir);
    setText([head, ...body].join("\n")); setPv(null); setRes(null);
    if (list[0].mapping) setMap(list[0].mapping);
    for (const d of list.filter((x) => (x.baslik || "") === head)) await api.delete(`/analiz/drafts/${d.id}`);
    loadDrafts(); window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`${body.length} SATIR FORMA ALINDI — DÜZELTİP BAŞLIKLARI BUL › HESAPLA`);
  };

  return (
    <div data-testid="analiz-page" className="space-y-3 p-4">
      <div className="flex items-center gap-2"><FileSearch className="h-6 w-6 text-orange-400" /><h1 className="font-head text-2xl font-bold text-white">ANALİZ FORMU</h1></div>
      <p className="text-xs text-slate-400">FİRMA ADINI VE SİTESİNİ YAZIN, GÖNDERİ VERİSİNİ BAŞLIKLARIYLA YAPIŞTIRIN. BAŞLIKLARIN SIRASI FARK ETMEZ — SİSTEM YERLERİNE KOYAR, EKSİĞİ ELLE SEÇEBİLİRSİNİZ. VERİDE FİRMA SÜTUNU VARSA HER FİRMA AYRI HESAPLANIR.</p>
      <div className="flex gap-2">
        <Input data-testid="analiz-firma" value={firma} onChange={(e) => setFirma(e.target.value)} placeholder="FİRMA ADI (VERİDE FİRMA SÜTUNU YOKSA)" className="h-9 border-slate-700 bg-slate-800 text-white" />
        <Input data-testid="analiz-web" value={web} onChange={(e) => setWeb(e.target.value)} placeholder="WEB SİTESİ" className="h-9 border-slate-700 bg-slate-800 text-white" />
      </div>
      <textarea data-testid="analiz-text" value={text} onChange={(e) => { setText(e.target.value); setPv(null); setRes(null); }} rows={8} placeholder={"BAŞLIKLARLA BİRLİKTE YAPIŞTIRIN (EXCEL'DEN KOPYALA-YAPIŞTIR)\nÖRN: ADET\tALICI ŞUBE\tGÖNDEREN ŞUBE\tKG\tDESİ\tTOPLAM YEKÜN"} className="w-full rounded-md border border-slate-700 bg-slate-800 p-2 font-mono text-xs text-slate-100" />
      <Button data-testid="analiz-preview" disabled={!text.trim()} onClick={preview} className="bg-blue-600 hover:bg-blue-700"><FileSearch className="mr-1 h-4 w-4" /> BAŞLIKLARI BUL</Button>

      {pv && (
        <div data-testid="analiz-mapping" className="space-y-2 rounded-lg border border-slate-800 bg-slate-900 p-3">
          <div className="text-sm font-bold text-white">BAŞLIK EŞLEŞTİRME — {fmt(pv.rows)} SATIR</div>
          <div className="overflow-auto">
            <table className="text-xs">
              <thead><tr>{pv.headers.map((h, i) => (
                <th key={i} className="min-w-[140px] p-1 text-left align-top">
                  <div className="mb-1 truncate text-slate-300">{h || `SÜTUN ${i + 1}`}</div>
                  <select data-testid={`analiz-map-${i}`} value={map[i] || ""} onChange={(e) => setMap({ ...map, [i]: e.target.value })} className={`w-full rounded border px-1 py-1 text-[11px] ${map[i] ? "border-emerald-700 bg-emerald-950 text-emerald-200" : "border-slate-700 bg-slate-800 text-slate-400"}`}>
                    <option value="">— KULLANMA —</option>
                    {Object.entries(pv.fields).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                  </select>
                </th>))}</tr></thead>
              <tbody>{pv.sample.map((r, ri) => <tr key={ri} className="border-t border-slate-800 text-slate-400">{pv.headers.map((_, ci) => <td key={ci} className="truncate p-1">{r[ci]}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button data-testid="analiz-run" disabled={busy} onClick={() => run(false)} className="bg-orange-600 hover:bg-orange-700"><Play className="mr-1 h-4 w-4" /> HESAPLA (ÖNİZLE)</Button>
            {res && <Button data-testid="analiz-save" disabled={busy} onClick={() => run(true)} className="bg-emerald-600 hover:bg-emerald-700"><Save className="mr-1 h-4 w-4" /> KARTLARA KAYDET</Button>}
          </div>
        </div>
      )}

      {res && (
        <div className="space-y-2">
          {res.skipped.length > 0 && <div data-testid="analiz-skipped" className="rounded border border-rose-800 bg-rose-950/40 px-2 py-1.5 text-xs font-bold text-rose-200">{res.skipped.length} SATIR İŞLENEMEDİ — KAYDEDİNCE TASLAKLARA DÜŞER ({[...new Set(res.skipped.map((s) => s.neden))].join(", ")})</div>}
          {edited && <div data-testid="analiz-edited" className="flex items-center gap-2 rounded border border-sky-800 bg-sky-950/40 px-2 py-1.5 text-xs font-bold text-sky-200">SATIRLARDA DEĞİŞİKLİK YAPTINIZ <Button data-testid="analiz-rerun" onClick={() => { setEdited(false); run(false); }} className="h-6 bg-orange-600 px-2 text-[11px] hover:bg-orange-700">YENİDEN HESAPLA</Button></div>}
          {res.firms.map((f, i) => <FirmResult key={f.name} f={f} i={i} rowsProps={pv ? { headers: pv.headers, lines: text.split("\n").filter((l) => l.trim()), onEdit: editCell } : null} />)}
        </div>
      )}

      {fdrafts.length > 0 && <FirmDrafts fdrafts={fdrafts} reload={loadDrafts} />}
      {drafts.length > 0 && (
        <details data-testid="analiz-drafts" className="rounded-lg border border-rose-900 bg-rose-950/20 p-3 text-xs">
          <summary className="cursor-pointer font-bold text-rose-200">TASLAKLAR — İŞLENEMEYEN SATIRLAR ({drafts.length})</summary>
          <div className="my-2 flex justify-end gap-2"><Button data-testid="analiz-drafts-toform-all" onClick={() => toForm(drafts)} className="h-7 bg-blue-700 px-2 text-xs hover:bg-blue-600">TÜMÜNÜ FORMA AL</Button><Button data-testid="analiz-drafts-clear" onClick={() => delDraft("all")} className="h-7 bg-rose-800 px-2 text-xs hover:bg-rose-700">TÜMÜNÜ SİL</Button></div>
          <div className="max-h-64 overflow-auto">
            {drafts.map((d) => (
              <div key={d.id} data-testid={`analiz-draft-${d.id}`} className="flex items-center gap-2 border-b border-rose-900/40 py-1">
                <span className="w-32 text-slate-500">{new Date(d.at).toLocaleString("tr-TR")}</span>
                <span className="w-40 font-bold text-rose-300">{d.neden}</span>
                <input data-testid={`analiz-draft-edit-${d.id}`} value={edits[d.id] ?? d.satir} onChange={(e) => setEdits({ ...edits, [d.id]: e.target.value })} title="DÜZENLE" className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-900 px-1 py-0.5 font-mono text-slate-200" />
                <Button data-testid={`analiz-draft-toform-${d.id}`} onClick={() => toForm([d])} className="h-6 bg-blue-700 px-2 text-[11px] hover:bg-blue-600">FORMA AL</Button>
                <button data-testid={`analiz-draft-del-${d.id}`} onClick={() => delDraft(d.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
